import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useNavigateToPost } from "../../hooks/useNavigateToPost"
import { SmartDateFormat } from "../../util/dates"
import FreeTextFormat from "../shared/FreeTextFormat"
import { stripText } from "../../util/strings"
import CommentSectionPreview from "../comments/CommentSectionPreview"
import { ShareUrlButton } from "../ShareUrlButton"
import { ShowHideButton } from "../shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "../RelatedPosts"
import type { HeadlineDisplayConfig } from "../../types/headlineDisplay"

const styles = {
  header: "text-base md:text-2xl font-bold text-black-800 leading-none md:leading-tight",
  bandName: "text-gray-500 leading-none text-xs",
  postDate: "text-xs md:text-sm text-black-800 leading-none md:leading-normal",
  externalLink: "text-xs md:text-sm text-gray-500 italic",
  description: "text-xs md:text-sm text-gray-500 mt-1",
}

interface HeadlineListItemShortProps {
  headline: any
  adminMode?: boolean
  updateStatus?: () => void
  config?: HeadlineDisplayConfig
}


export const HeadlineListItemShort = ({
  headline,
  adminMode = false,
  updateStatus = () => {},
  config = { showComments: "full", showRelatedPosts: "full" },
}: HeadlineListItemShortProps) => {
  const { showComments, showRelatedPosts } = config
  const [postStatus, setPostStatus] = useState(headline.status)
  const navigateToPostScreen = useNavigateToPost(headline.nada_path)

  const rawDescription = headline.original_text
    ? headline.description
      ? `${headline.description}:\n${headline.original_text}`
      : headline.original_text
    : `Headline from ${headline.band_name}`
  const description = stripText(rawDescription)

  return (
    <div>
      <div className="flex gap-3 items-start">
        <div className="flex-1 min-w-0">
          <h2
            className={styles.header + " cursor-pointer"}
            onClick={navigateToPostScreen}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: headline.title
                  ? headline.title_cleaned || headline.title
                  : `New Headline from ${headline.band_name}`,
              }}
            />
            <ShareUrlButton nadaUrl={headline.nada_url} renderMode="icon" alignment="align-middle" />
          </h2>

          <Link className={styles.bandName} to={headline.local_path}>
            {headline.band_name}
          </Link>

          <div className={styles.postDate}>
            Posted:&nbsp;
            {SmartDateFormat({ dateStr: headline.published_at, includeTime: false })}
          </div>
        </div>

        {headline.external_photo_src && (
          <div className="hidden sm:block w-1/4 flex-shrink-0">
            <img
              src={headline.external_photo_src}
              alt={headline.title_cleaned || headline.title}
              onClick={navigateToPostScreen}
              className="aspect-square object-cover w-full cursor-pointer"
            />
          </div>
        )}
      </div>

      <div className="mt-2">
        <div className={styles.description}>
          <FreeTextFormat
            text={description}
            expandable={false}
            expanded={false}
            linkify={false}
          />
        </div>

        <RelatedPosts postId={headline.id} nadaUrl={headline.nada_url} mode={showRelatedPosts} />
        <div className="mt-6">
          <ReactionBlock
            contentType="Post"
            contentId={headline.id}
            postId={headline.id}
          />
        </div>
        <CommentSectionPreview postId={headline.id} postPath={headline.nada_url} mode={showComments} />
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
      </div>
    </div>
  )
}
